import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  Dimmer,
  Dropdown,
  Form,
  Header,
  Icon,
  Input,
  Loader,
  Message,
  Modal,
  Table,
} from 'semantic-ui-react';
import GlassPageWrapper from '../../components/Layout/GlassPageWrapper';
import GlassTable from '../../components/Common/GlassTable';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/useToast';
import { getButtonHoverEffects, getDefaultButtonStyle } from '../../styles/designSystem';
import {
  handsetDeviceService,
  type HandsetDeviceDto,
} from '../../services/handsetDevices/handsetDeviceService';
import {
  HANDSET_CLASSIFICACOES,
  HANDSET_STATUS_ESTOQUE,
  HANDSET_TECNOLOGIAS,
  distinctHandsetField,
  filterHandsetCatalog,
} from '../../utils/handsetCatalog';

const PARCELAS = 24;

const emptyCreateForm = () => ({
  nome: '',
  marca: '',
  classificacao: 'SMARTPHONE',
  nomeComercial: '',
  tecnologia: '5G - VoLTE',
  statusEstoque: 'Disponivel',
  valorTotal: '',
  valorMensal: '',
  ativo: true,
});

function syncFromTotal(total: number): { total: number; mensal: number } {
  const t = Number(total) || 0;
  return {
    total: t,
    mensal: t > 0 ? Math.round((t / PARCELAS) * 100) / 100 : 0,
  };
}

function syncFromMensal(mensal: number): { total: number; mensal: number } {
  const m = Number(mensal) || 0;
  return {
    mensal: m,
    total: m > 0 ? Math.round(m * PARCELAS * 100) / 100 : 0,
  };
}

function rowToPayload(row: HandsetDeviceDto) {
  return {
    nome: row.nome.trim(),
    marca: row.marca.trim(),
    classificacao: row.classificacao.trim(),
    nomeComercial: row.nomeComercial.trim(),
    tecnologia: row.tecnologia.trim(),
    statusEstoque: row.statusEstoque.trim(),
    valorTotal: row.valorTotal,
    valorMensal: row.valorMensal,
    ativo: row.ativo,
  };
}

const HandsetDevicesPage: React.FC = () => {
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const canManage =
    user?.roles?.includes('Admin') || user?.roles?.includes('TeamLeader');

  const [items, setItems] = useState<HandsetDeviceDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyCreateForm);
  const [filters, setFilters] = useState({
    marca: '',
    classificacao: '',
    tecnologia: '',
    statusEstoque: '',
    busca: '',
    apenasAtivos: false,
  });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await handsetDeviceService.list();
      setItems(data);
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Erro ao carregar aparelhos.');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    void load();
  }, [load]);

  const filteredItems = useMemo(
    () =>
      filterHandsetCatalog(items, {
        marca: filters.marca || undefined,
        classificacao: filters.classificacao || undefined,
        tecnologia: filters.tecnologia || undefined,
        statusEstoque: filters.statusEstoque || undefined,
        busca: filters.busca || undefined,
      }, { apenasAtivos: filters.apenasAtivos }),
    [items, filters]
  );

  const marcaOptions = useMemo(
    () => distinctHandsetField(items, 'marca').map((m) => ({ key: m, text: m, value: m })),
    [items]
  );

  const saveRow = async (row: HandsetDeviceDto) => {
    setSavingId(row.id);
    try {
      const updated = await handsetDeviceService.update(row.id, rowToPayload(row));
      setItems((prev) => prev.map((i) => (i.id === row.id ? updated : i)));
      showSuccess('Aparelho atualizado.');
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Erro ao gravar.');
    } finally {
      setSavingId(null);
    }
  };

  const handleCreate = async () => {
    const nome = createForm.nome.trim();
    const marca = createForm.marca.trim();
    const nomeComercial = createForm.nomeComercial.trim();
    if (!nome || !marca || !nomeComercial) {
      showError('Preencha descrição (PDF), marca e nome comercial.');
      return;
    }
    const { total, mensal } =
      createForm.valorTotal !== ''
        ? syncFromTotal(Number(createForm.valorTotal))
        : syncFromMensal(Number(createForm.valorMensal));
    try {
      const created = await handsetDeviceService.create({
        nome,
        marca,
        classificacao: createForm.classificacao,
        nomeComercial,
        tecnologia: createForm.tecnologia,
        statusEstoque: createForm.statusEstoque,
        valorTotal: total,
        valorMensal: mensal,
        ativo: createForm.ativo,
      });
      setItems((prev) =>
        [...prev, created].sort((a, b) =>
          `${a.marca}${a.nomeComercial}`.localeCompare(`${b.marca}${b.nomeComercial}`, 'pt-BR')
        )
      );
      setCreateOpen(false);
      setCreateForm(emptyCreateForm());
      showSuccess('Aparelho criado.');
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Erro ao criar.');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Excluir este aparelho do catálogo?')) return;
    try {
      await handsetDeviceService.remove(id);
      setItems((prev) => prev.filter((i) => i.id !== id));
      showSuccess('Aparelho removido.');
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Erro ao excluir.');
    }
  };

  const patchRow = (id: string, patch: Partial<HandsetDeviceDto>) => {
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, ...patch } : i)));
  };

  const selectOptions = (values: readonly string[]) =>
    values.map((v) => ({ key: v, text: v, value: v }));

  return (
    <GlassPageWrapper>
      <Header as="h2" style={{ color: '#fff', marginBottom: '1rem' }}>
        <Icon name="mobile alternate" />
        Catálogo de aparelhos
      </Header>

      {!canManage && (
        <Message info size="small">
          Visualização apenas. Apenas Admin e Team Leader podem editar o catálogo.
        </Message>
      )}

      <Message size="small" style={{ marginBottom: 12 }}>
        Valores do book <strong>não incluem chip (R$ 7,00)</strong>. Campo <strong>Descrição (PDF)</strong> =
        texto do contrato; <strong>Nome comercial</strong> = book.
      </Message>

      <Form style={{ marginBottom: 16 }}>
        <Form.Group widths="equal">
          <Form.Field>
            <label>Busca</label>
            <Input
              icon="search"
              placeholder="Nome, comercial ou marca..."
              value={filters.busca}
              onChange={(_, { value }) => setFilters((f) => ({ ...f, busca: String(value) }))}
            />
          </Form.Field>
          <Form.Field>
            <label>Marca</label>
            <Dropdown
              clearable
              selection
              search
              placeholder="Todas"
              options={marcaOptions}
              value={filters.marca || undefined}
              onChange={(_, { value }) => setFilters((f) => ({ ...f, marca: String(value ?? '') }))}
            />
          </Form.Field>
          <Form.Field>
            <label>Classificação</label>
            <Dropdown
              clearable
              selection
              placeholder="Todas"
              options={selectOptions(HANDSET_CLASSIFICACOES)}
              value={filters.classificacao || undefined}
              onChange={(_, { value }) =>
                setFilters((f) => ({ ...f, classificacao: String(value ?? '') }))
              }
            />
          </Form.Field>
          <Form.Field>
            <label>Tecnologia</label>
            <Dropdown
              clearable
              selection
              placeholder="Todas"
              options={selectOptions(HANDSET_TECNOLOGIAS)}
              value={filters.tecnologia || undefined}
              onChange={(_, { value }) =>
                setFilters((f) => ({ ...f, tecnologia: String(value ?? '') }))
              }
            />
          </Form.Field>
          <Form.Field>
            <label>Status book</label>
            <Dropdown
              clearable
              selection
              placeholder="Todos"
              options={selectOptions(HANDSET_STATUS_ESTOQUE)}
              value={filters.statusEstoque || undefined}
              onChange={(_, { value }) =>
                setFilters((f) => ({ ...f, statusEstoque: String(value ?? '') }))
              }
            />
          </Form.Field>
        </Form.Group>
        <Checkbox
          toggle
          label="Somente ativos no catálogo"
          checked={filters.apenasAtivos}
          onChange={(_, { checked }) =>
            setFilters((f) => ({ ...f, apenasAtivos: !!checked }))
          }
        />
      </Form>

      {canManage && (
        <Button
          primary
          style={{ ...getDefaultButtonStyle(), marginBottom: 16 }}
          {...getButtonHoverEffects('default')}
          onClick={() => setCreateOpen(true)}
        >
          <Icon name="plus" />
          Novo aparelho
        </Button>
      )}

      <Dimmer active={loading} inverted>
        <Loader>Carregando...</Loader>
      </Dimmer>

      <GlassTable>
        <Table celled striped compact size="small">
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Marca</Table.HeaderCell>
              <Table.HeaderCell>Nome comercial</Table.HeaderCell>
              <Table.HeaderCell>Descrição (PDF)</Table.HeaderCell>
              <Table.HeaderCell>Classif.</Table.HeaderCell>
              <Table.HeaderCell>Tecn.</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
              <Table.HeaderCell>Total (R$)</Table.HeaderCell>
              <Table.HeaderCell>VF 24x (R$)</Table.HeaderCell>
              <Table.HeaderCell collapsing>Ativo</Table.HeaderCell>
              {canManage && <Table.HeaderCell collapsing>Ações</Table.HeaderCell>}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {filteredItems.map((row) => (
              <Table.Row key={row.id} style={row.ativo ? undefined : { opacity: 0.65 }}>
                <Table.Cell>
                  {canManage ? (
                    <Input
                      fluid
                      value={row.marca}
                      onChange={(_, { value }) => patchRow(row.id, { marca: String(value) })}
                      onBlur={() => void saveRow(row)}
                    />
                  ) : (
                    row.marca
                  )}
                </Table.Cell>
                <Table.Cell>
                  {canManage ? (
                    <Input
                      fluid
                      value={row.nomeComercial}
                      onChange={(_, { value }) =>
                        patchRow(row.id, { nomeComercial: String(value) })
                      }
                      onBlur={() => void saveRow(row)}
                    />
                  ) : (
                    row.nomeComercial
                  )}
                </Table.Cell>
                <Table.Cell>
                  {canManage ? (
                    <Input
                      fluid
                      value={row.nome}
                      onChange={(_, { value }) => patchRow(row.id, { nome: String(value) })}
                      onBlur={() => void saveRow(row)}
                    />
                  ) : (
                    row.nome
                  )}
                </Table.Cell>
                <Table.Cell>
                  {canManage ? (
                    <Dropdown
                      fluid
                      selection
                      options={selectOptions(HANDSET_CLASSIFICACOES)}
                      value={row.classificacao}
                      onChange={(_, { value }) =>
                        patchRow(row.id, { classificacao: String(value) })
                      }
                      onBlur={() => void saveRow(row)}
                    />
                  ) : (
                    row.classificacao
                  )}
                </Table.Cell>
                <Table.Cell>
                  {canManage ? (
                    <Dropdown
                      fluid
                      selection
                      options={selectOptions(HANDSET_TECNOLOGIAS)}
                      value={row.tecnologia}
                      onChange={(_, { value }) =>
                        patchRow(row.id, { tecnologia: String(value) })
                      }
                      onBlur={() => void saveRow(row)}
                    />
                  ) : (
                    row.tecnologia
                  )}
                </Table.Cell>
                <Table.Cell>
                  {canManage ? (
                    <Dropdown
                      fluid
                      selection
                      options={selectOptions(HANDSET_STATUS_ESTOQUE)}
                      value={row.statusEstoque}
                      onChange={(_, { value }) => {
                        const status = String(value);
                        patchRow(row.id, {
                          statusEstoque: status,
                          ativo: status !== 'Indisponivel',
                        });
                      }}
                      onClose={() => void saveRow(row)}
                    />
                  ) : (
                    row.statusEstoque
                  )}
                </Table.Cell>
                <Table.Cell>
                  {canManage ? (
                    <Input
                      type="number"
                      min={0}
                      step="0.01"
                      fluid
                      value={row.valorTotal}
                      onChange={(_, { value }) => {
                        const synced = syncFromTotal(Number(value));
                        patchRow(row.id, {
                          valorTotal: synced.total,
                          valorMensal: synced.mensal,
                        });
                      }}
                      onBlur={() => void saveRow(row)}
                    />
                  ) : (
                    row.valorTotal.toFixed(2)
                  )}
                </Table.Cell>
                <Table.Cell>
                  {canManage ? (
                    <Input
                      type="number"
                      min={0}
                      step="0.01"
                      fluid
                      value={row.valorMensal}
                      onChange={(_, { value }) => {
                        const synced = syncFromMensal(Number(value));
                        patchRow(row.id, {
                          valorTotal: synced.total,
                          valorMensal: synced.mensal,
                        });
                      }}
                      onBlur={() => void saveRow(row)}
                    />
                  ) : (
                    row.valorMensal.toFixed(2)
                  )}
                </Table.Cell>
                <Table.Cell textAlign="center">
                  <Checkbox
                    toggle
                    checked={row.ativo}
                    disabled={!canManage || savingId === row.id}
                    onMouseDown={(e: React.MouseEvent) => e.preventDefault()}
                    onChange={(_, { checked }) => {
                      const next = { ...row, ativo: !!checked };
                      patchRow(row.id, { ativo: !!checked });
                      void saveRow(next);
                    }}
                  />
                </Table.Cell>
                {canManage && (
                  <Table.Cell>
                    <Button
                      icon="trash"
                      color="red"
                      size="mini"
                      loading={savingId === row.id}
                      onClick={() => void handleDelete(row.id)}
                    />
                  </Table.Cell>
                )}
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </GlassTable>

      {!loading && filteredItems.length === 0 && (
        <Message warning>Nenhum aparelho encontrado com os filtros atuais.</Message>
      )}

      <Modal open={createOpen} onClose={() => setCreateOpen(false)} size="small">
        <Modal.Header>Novo aparelho</Modal.Header>
        <Modal.Content scrolling>
          <Form>
            <Form.Field required>
              <label>Descrição (PDF / contrato)</label>
              <Input
                value={createForm.nome}
                onChange={(_, { value }) => setCreateForm((f) => ({ ...f, nome: String(value) }))}
              />
            </Form.Field>
            <Form.Field required>
              <label>Marca</label>
              <Input
                value={createForm.marca}
                onChange={(_, { value }) => setCreateForm((f) => ({ ...f, marca: String(value) }))}
              />
            </Form.Field>
            <Form.Field required>
              <label>Nome comercial</label>
              <Input
                value={createForm.nomeComercial}
                onChange={(_, { value }) =>
                  setCreateForm((f) => ({ ...f, nomeComercial: String(value) }))
                }
              />
            </Form.Field>
            <Form.Group widths="equal">
              <Form.Field>
                <label>Classificação</label>
                <Dropdown
                  selection
                  options={selectOptions(HANDSET_CLASSIFICACOES)}
                  value={createForm.classificacao}
                  onChange={(_, { value }) =>
                    setCreateForm((f) => ({ ...f, classificacao: String(value) }))
                  }
                />
              </Form.Field>
              <Form.Field>
                <label>Tecnologia</label>
                <Dropdown
                  selection
                  options={selectOptions(HANDSET_TECNOLOGIAS)}
                  value={createForm.tecnologia}
                  onChange={(_, { value }) =>
                    setCreateForm((f) => ({ ...f, tecnologia: String(value) }))
                  }
                />
              </Form.Field>
              <Form.Field>
                <label>Status book</label>
                <Dropdown
                  selection
                  options={selectOptions(HANDSET_STATUS_ESTOQUE)}
                  value={createForm.statusEstoque}
                  onChange={(_, { value }) => {
                    const status = String(value);
                    setCreateForm((f) => ({
                      ...f,
                      statusEstoque: status,
                      ativo: status !== 'Indisponivel',
                    }));
                  }}
                />
              </Form.Field>
            </Form.Group>
            <Form.Field>
              <label>Valor total (R$)</label>
              <Input
                type="number"
                min={0}
                step="0.01"
                value={createForm.valorTotal}
                onChange={(_, { value }) => {
                  const synced = syncFromTotal(Number(value));
                  setCreateForm((f) => ({
                    ...f,
                    valorTotal: String(value),
                    valorMensal: synced.mensal ? String(synced.mensal) : '',
                  }));
                }}
              />
            </Form.Field>
            <Form.Field>
              <label>Parcela mensal VF 24x (R$)</label>
              <Input
                type="number"
                min={0}
                step="0.01"
                value={createForm.valorMensal}
                onChange={(_, { value }) => {
                  const synced = syncFromMensal(Number(value));
                  setCreateForm((f) => ({
                    ...f,
                    valorMensal: String(value),
                    valorTotal: synced.total ? String(synced.total) : '',
                  }));
                }}
              />
            </Form.Field>
            <Form.Field>
              <Checkbox
                toggle
                label="Ativo no catálogo"
                checked={createForm.ativo}
                onChange={(_, { checked }) =>
                  setCreateForm((f) => ({ ...f, ativo: !!checked }))
                }
              />
            </Form.Field>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setCreateOpen(false)}>Cancelar</Button>
          <Button primary onClick={() => void handleCreate()}>
            Criar
          </Button>
        </Modal.Actions>
      </Modal>
    </GlassPageWrapper>
  );
};

export default HandsetDevicesPage;
